import React, { useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Drawer,
  Form,
  Input,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  HeroKpiCard,
  OiEmptyState,
  OiPageFrame,
  PrimaryKpiCard,
  SignalBadge,
} from '@/components/owner-intelligence/OwnerIntelligenceComponents';
import { eoafService, type EoafActionSummary } from '@/services/eoaf.service';

function priorityTier(p: string): 'critical' | 'watch' | 'healthy' {
  if (p === 'critical' || p === 'high') return 'critical';
  if (p === 'medium') return 'watch';
  return 'healthy';
}

const OperationalActionsPage: React.FC = () => {
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(params.get('id'));
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const dashQ = useQuery({
    queryKey: ['eoaf-dashboard'],
    queryFn: () => eoafService.getDashboard(),
    staleTime: 60_000,
  });
  const listQ = useQuery({
    queryKey: ['eoaf-actions', statusFilter],
    queryFn: () =>
      eoafService.listActions(statusFilter === 'all' ? undefined : { status: statusFilter }),
    staleTime: 30_000,
  });
  const playbooksQ = useQuery({
    queryKey: ['eoaf-playbooks'],
    queryFn: () => eoafService.listPlaybooks(),
    staleTime: 120_000,
  });
  const detailQ = useQuery({
    queryKey: ['eoaf-action', selectedId],
    queryFn: () => eoafService.getAction(selectedId!),
    enabled: Boolean(selectedId),
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['eoaf-dashboard'] });
    void qc.invalidateQueries({ queryKey: ['eoaf-actions'] });
    void qc.invalidateQueries({ queryKey: ['eoaf-action'] });
  };

  const createMut = useMutation({
    mutationFn: eoafService.createAction,
    onSuccess: () => {
      message.success('Action created');
      refresh();
    },
    onError: (e: Error) => message.error(e.message),
  });

  const runMut = useMutation({
    mutationFn: async ({
      id,
      op,
    }: {
      id: string;
      op: 'accept' | 'start' | 'pause' | 'complete';
    }) => {
      if (op === 'accept') return eoafService.accept(id);
      if (op === 'start') return eoafService.start(id);
      if (op === 'pause') return eoafService.pause(id);
      return eoafService.complete(id);
    },
    onSuccess: () => {
      message.success('Updated');
      refresh();
    },
    onError: (e: Error) => message.error(e.message),
  });

  const stepMut = useMutation({
    mutationFn: ({ id, stepId }: { id: string; stepId: string }) =>
      eoafService.completeStep(id, stepId),
    onSuccess: () => {
      message.success('Step completed');
      refresh();
    },
    onError: (e: Error) => message.error(e.message),
  });

  const commentMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) => eoafService.comment(id, body),
    onSuccess: () => {
      message.success('Comment added');
      refresh();
    },
    onError: (e: Error) => message.error(e.message),
  });

  const outcomeMut = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      eoafService.outcome(id, payload),
    onSuccess: () => {
      message.success('Outcome recorded');
      refresh();
    },
    onError: (e: Error) => message.error(e.message),
  });

  const actions = listQ.data || [];
  const dash = dashQ.data;
  const detail = detailQ.data as
    | {
        action?: Record<string, unknown>;
        steps?: Array<Record<string, unknown>>;
        progress?: { pct: number; requiredPending: number };
        timeline?: Array<Record<string, unknown>>;
        comments?: Array<Record<string, unknown>>;
      }
    | undefined;

  const deptRows = useMemo(
    () =>
      Object.entries(dash?.byDepartment || {}).map(([department, open]) => ({
        department,
        open,
      })),
    [dash?.byDepartment]
  );

  return (
    <OiPageFrame
      subtitle="Convert intelligence into owned operational actions — people execute; NOBLEX measures. Never posts ERP."
      loading={dashQ.isLoading && !dash}
      error={Boolean(dashQ.error)}
      errorMessage="Operational actions unavailable"
    >
      {dash?.degrade?.jobs_disabled ? (
        <Card size="small" style={{ marginBottom: 12 }}>
          EOAF jobs disabled (EOAF_JOBS_DISABLED=1). Escalation/SLA scans paused.
        </Card>
      ) : null}

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={6}>
          <HeroKpiCard label="Open actions" value={String(dash?.openCount ?? '—')} />
        </Col>
        <Col xs={12} md={4}>
          <PrimaryKpiCard label="Critical" value={dash?.criticalCount ?? '—'} />
        </Col>
        <Col xs={12} md={4}>
          <PrimaryKpiCard label="Overdue" value={dash?.overdueCount ?? '—'} />
        </Col>
        <Col xs={12} md={4}>
          <PrimaryKpiCard label="Completed" value={dash?.completedCount ?? '—'} />
        </Col>
        <Col xs={12} md={3}>
          <PrimaryKpiCard
            label="Eff. score"
            value={dash?.effectiveness?.avgScore != null ? dash.effectiveness.avgScore : '—'}
          />
        </Col>
        <Col xs={12} md={3}>
          <PrimaryKpiCard
            label="Cost avoided"
            value={dash?.effectiveness?.costAvoided != null ? dash.effectiveness.costAvoided : '—'}
          />
        </Col>
      </Row>

      <Tabs
        items={[
          {
            key: 'board',
            label: 'Actions',
            children: (
              <Card
                bordered={false}
                title="Operational actions"
                extra={
                  <Select
                    value={statusFilter}
                    onChange={setStatusFilter}
                    style={{ width: 160 }}
                    options={[
                      { value: 'all', label: 'All statuses' },
                      { value: 'suggested', label: 'Suggested' },
                      { value: 'accepted', label: 'Accepted' },
                      { value: 'in_progress', label: 'In progress' },
                      { value: 'escalated', label: 'Escalated' },
                      { value: 'completed', label: 'Completed' },
                    ]}
                  />
                }
              >
                {!actions.length ? (
                  <OiEmptyState
                    title="No actions yet"
                    body="Accept an EOIP recommendation or create a manual action."
                  />
                ) : (
                  <Table
                    size="small"
                    rowKey="id"
                    dataSource={actions}
                    onRow={(row) => ({
                      onClick: () => setSelectedId(row.id),
                      style: { cursor: 'pointer' },
                    })}
                    columns={[
                      {
                        title: 'Priority',
                        dataIndex: 'priority',
                        width: 100,
                        render: (p: string) => <SignalBadge tier={priorityTier(p)} />,
                      },
                      { title: 'Status', dataIndex: 'status', width: 110 },
                      { title: 'Title', dataIndex: 'title' },
                      { title: 'Domain', dataIndex: 'domain', width: 110 },
                      { title: 'Owner role', dataIndex: 'ownerRole', width: 140 },
                      {
                        title: 'Due',
                        dataIndex: 'dueDate',
                        width: 110,
                        render: (v: string) => (v ? String(v).slice(0, 10) : '—'),
                      },
                    ]}
                  />
                )}
              </Card>
            ),
          },
          {
            key: 'mine',
            label: 'My / Critical',
            children: (
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Card title="My actions" bordered={false}>
                    <Table
                      size="small"
                      rowKey="id"
                      dataSource={dash?.myActions || []}
                      onRow={(row: EoafActionSummary) => ({
                        onClick: () => setSelectedId(row.id),
                        style: { cursor: 'pointer' },
                      })}
                      columns={[
                        { title: 'Title', dataIndex: 'title' },
                        { title: 'Status', dataIndex: 'status', width: 100 },
                      ]}
                      locale={{ emptyText: 'No actions assigned to you' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card title="Critical & overdue" bordered={false}>
                    <Table
                      size="small"
                      rowKey="id"
                      dataSource={[...(dash?.critical || []), ...(dash?.overdue || [])]}
                      onRow={(row: EoafActionSummary) => ({
                        onClick: () => setSelectedId(row.id),
                        style: { cursor: 'pointer' },
                      })}
                      columns={[
                        { title: 'Title', dataIndex: 'title' },
                        { title: 'Priority', dataIndex: 'priority', width: 90 },
                        { title: 'Status', dataIndex: 'status', width: 100 },
                      ]}
                    />
                  </Card>
                </Col>
              </Row>
            ),
          },
          {
            key: 'dept',
            label: 'By department',
            children: (
              <Card bordered={false}>
                <Table
                  size="small"
                  rowKey="department"
                  dataSource={deptRows}
                  columns={[
                    { title: 'Department', dataIndex: 'department' },
                    { title: 'Open', dataIndex: 'open' },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'playbooks',
            label: 'Playbooks',
            children: (
              <Card title="Configurable playbooks" bordered={false}>
                <Table
                  size="small"
                  rowKey="id"
                  dataSource={(playbooksQ.data as Array<Record<string, unknown>>) || []}
                  expandable={{
                    expandedRowRender: (row) => (
                      <ol>
                        {((row.steps as Array<Record<string, unknown>>) || []).map((s) => (
                          <li key={String(s.id)}>
                            {String(s.title)}
                            {s.is_required ? <Tag color="blue">required</Tag> : null}
                          </li>
                        ))}
                      </ol>
                    ),
                  }}
                  columns={[
                    { title: 'Code', dataIndex: 'code', width: 140 },
                    { title: 'Title', dataIndex: 'title' },
                    { title: 'Domain', dataIndex: 'domain', width: 120 },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'create',
            label: 'Create',
            children: (
              <Card title="Manual operational action" bordered={false}>
                <Form
                  layout="vertical"
                  onFinish={(v) =>
                    createMut.mutate({
                      title: v.title,
                      description: v.description,
                      domain: v.domain,
                      priority: v.priority,
                      playbookCode: v.playbookCode || undefined,
                    })
                  }
                >
                  <Form.Item name="title" label="Title" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                  <Form.Item name="description" label="Description">
                    <Input.TextArea rows={3} />
                  </Form.Item>
                  <Form.Item name="domain" label="Domain" initialValue="operations">
                    <Select
                      options={[
                        'operations',
                        'procurement',
                        'finance',
                        'manufacturing',
                        'quality',
                        'sales',
                        'projects',
                      ].map((x) => ({ value: x, label: x }))}
                    />
                  </Form.Item>
                  <Form.Item name="priority" label="Priority" initialValue="medium">
                    <Select
                      options={['critical', 'high', 'medium', 'low'].map((x) => ({
                        value: x,
                        label: x,
                      }))}
                    />
                  </Form.Item>
                  <Form.Item name="playbookCode" label="Playbook">
                    <Select
                      allowClear
                      options={[
                        { value: 'supplier_delay', label: 'supplier_delay' },
                        { value: 'cashflow_risk', label: 'cashflow_risk' },
                      ]}
                    />
                  </Form.Item>
                  <Button type="primary" htmlType="submit" loading={createMut.isPending}>
                    Create action
                  </Button>
                </Form>
              </Card>
            ),
          },
        ]}
      />

      <Drawer
        width={560}
        open={Boolean(selectedId)}
        onClose={() => setSelectedId(null)}
        title={String(detail?.action?.title || 'Action detail')}
      >
        {detail?.action ? (
          <>
            <Space wrap style={{ marginBottom: 12 }}>
              <Tag>{String(detail.action.status)}</Tag>
              <Tag color="red">{String(detail.action.priority)}</Tag>
              <Tag>{String(detail.action.domain)}</Tag>
              {detail.progress ? <Tag>Playbook {detail.progress.pct}%</Tag> : null}
            </Space>
            <Space wrap style={{ marginBottom: 16 }}>
              <Button
                size="small"
                onClick={() => runMut.mutate({ id: String(detail.action!.id), op: 'accept' })}
              >
                Accept
              </Button>
              <Button
                size="small"
                onClick={() => runMut.mutate({ id: String(detail.action!.id), op: 'start' })}
              >
                Start
              </Button>
              <Button
                size="small"
                onClick={() => runMut.mutate({ id: String(detail.action!.id), op: 'pause' })}
              >
                Pause
              </Button>
              <Button
                size="small"
                type="primary"
                onClick={() => runMut.mutate({ id: String(detail.action!.id), op: 'complete' })}
              >
                Complete
              </Button>
            </Space>
            <Card size="small" title="Playbook steps" style={{ marginBottom: 12 }}>
              {(detail.steps || []).map((s) => (
                <div
                  key={String(s.id)}
                  style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}
                >
                  <span>
                    {String(s.step_order)}. {String(s.title)}{' '}
                    <Tag>{String(s.status)}</Tag>
                  </span>
                  {String(s.status) !== 'completed' ? (
                    <Button
                      size="small"
                      onClick={() =>
                        stepMut.mutate({
                          id: String(detail.action!.id),
                          stepId: String(s.id),
                        })
                      }
                    >
                      Done
                    </Button>
                  ) : null}
                </div>
              ))}
              {!detail.steps?.length ? <div>No playbook steps</div> : null}
            </Card>
            <Card size="small" title="Timeline" style={{ marginBottom: 12 }}>
              {(detail.timeline || []).slice(-12).map((t) => (
                <div key={String(t.id)} style={{ fontSize: 12, marginBottom: 4 }}>
                  <strong>{String(t.event_type)}</strong> — {String(t.message)}
                </div>
              ))}
            </Card>
            <Form
              layout="inline"
              onFinish={(v) =>
                commentMut.mutate({ id: String(detail.action!.id), body: v.body })
              }
              style={{ marginBottom: 12 }}
            >
              <Form.Item name="body" rules={[{ required: true }]}>
                <Input placeholder="Add comment" style={{ width: 280 }} />
              </Form.Item>
              <Button htmlType="submit" size="small">
                Comment
              </Button>
            </Form>
            <Form
              layout="vertical"
              onFinish={(v) =>
                outcomeMut.mutate({
                  id: String(detail.action!.id),
                  payload: {
                    actualResult: v.actualResult,
                    costSaved: v.costSaved ? Number(v.costSaved) : undefined,
                    effectiveness: {
                      recommendationUseful: true,
                      actionWorked: true,
                      recommendAgain: true,
                    },
                  },
                })
              }
            >
              <Form.Item name="actualResult" label="Actual result">
                <Input.TextArea rows={2} />
              </Form.Item>
              <Form.Item name="costSaved" label="Cost saved">
                <Input type="number" />
              </Form.Item>
              <Button htmlType="submit" size="small">
                Record outcome
              </Button>
            </Form>
          </>
        ) : (
          <OiEmptyState title="Loading…" body="" />
        )}
      </Drawer>
    </OiPageFrame>
  );
};

export default OperationalActionsPage;
